import { useState, useMemo } from "react";
import { useAccounting } from "@/contexts/AccountingContext";
import { useInventory } from "@/contexts/InventoryContext";
import { useAuth } from "@/contexts/AuthContext";
import { getAccountingPermissions } from "@/lib/accounting/engine";
import { StockItem, ItemType, ServiceBillingMethod } from "@/lib/accounting/types";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Plus, Search, Package, AlertTriangle, BarChart3, ArrowRightLeft,
  Layers, Clock, TrendingDown, TrendingUp, IndianRupee, Archive, RefreshCw,
  Zap, Sparkles, Edit, Trash2, CheckCircle2, ShieldCheck, Filter, Download
} from "lucide-react";
import { ProductPriceHistoryDialog } from "@/components/inventory/ProductPriceHistoryDialog";
import { InventoryReconciliationDialog } from "@/components/inventory/InventoryReconciliationDialog";
import { useCompany } from "@/contexts/CompanyContext";
import { useToast } from "@/hooks/use-toast";

function formatINR(v: number) {
  return `₹${(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const DEFAULT_SERVICE_CATEGORIES = [
  "Web Hosting",
  "Domain Registration",
  "Website Development",
  "Software Development",
  "Mobile App Dev",
  "Cybersecurity",
  "Pen Testing",
  "Digital Marketing",
  "SEO & Optimization",
  "Graphic Designing",
  "IT Consulting",
  "Tech Support",
  "Maintenance Contracts",
  "Cloud Services",
  "Software Licenses",
  "Consulting",
];

const DEFAULT_GOODS_CATEGORIES = [
  "Computers & Desktops",
  "Laptops & Notebooks",
  "Networking Equipment",
  "Peripherals & Accessories",
  "Office Electronics",
  "Raw Materials",
  "Finished Goods",
  "Consumables",
];

const GOODS_UNITS = ["Nos", "Pcs", "Kg", "Gm", "Ltr", "Mtr", "Ft", "Sq.Ft", "Box", "Set", "Pair", "Bag", "Ton", "Quintal"];
const SERVICE_UNITS = ["Projects", "Hours", "Days", "Months", "Years", "Services", "Sessions", "Visits", "Nos", "Units"];

export default function InventoryPage() {
  const { stockItems, stockGroups, units, addStockItem, updateStockItem, deleteStockItem, addStockGroup } = useAccounting();
  const {
    categories, batches, movements, transfers,
    addCategory, addBatch, getBatchesByItem, getExpiringBatches,
    adjustStock, transferStock, recordMovement, getMovementsByItem,
    getFIFOLayers, getFIFOValuation, getLowStockItems, getStockSummary,
    reconcileInventory,
  } = useInventory();
  const { role } = useAuth();
  const { activeCompany } = useCompany();
  const { toast } = useToast();
  const perms = getAccountingPermissions(role, "Inventory");

  // Filtering & search
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [stockStatusFilter, setStockStatusFilter] = useState<string>("all");
  const [reconciling, setReconciling] = useState(false);
  const [reconcileDialogOpen, setReconcileDialogOpen] = useState(false);

  const handleReconcileStock = async () => {
    setReconciling(true);
    try {
      const res = await reconcileInventory();
      if (res && res.success) {
        toast({
          title: "Stock Reconciled",
          description: `Verified ${res.totalGoodsItems || 0} goods items. ${res.movementsLinked || 0} movements linked, ${res.movementsCreated || 0} missing movements created.`,
        });
      } else {
        toast({ title: "Stock Reconciled", description: "All stock movements reconciled successfully." });
      }
    } catch (err: any) {
      toast({ title: "Reconciliation Error", description: err.message || "Failed to reconcile stock.", variant: "destructive" });
    } finally {
      setReconciling(false);
    }
  };

  // Create item dialog
  const [open, setOpen] = useState(false);
  const [itemType, setItemType] = useState<ItemType>("goods");
  const [name, setName] = useState("");
  const [stockGroup, setStockGroup] = useState("");
  const [customGroup, setCustomGroup] = useState("");
  const [unit, setUnit] = useState("Nos");
  const [hsnCode, setHsnCode] = useState("");
  const [sacCode, setSacCode] = useState("");
  const [billingMethod, setBillingMethod] = useState<ServiceBillingMethod>("one-time");
  const [gstRate, setGstRate] = useState(18);
  const [standardRate, setStandardRate] = useState(0); // Selling price
  const [costRate, setCostRate] = useState(0); // Purchase / direct cost
  const [openingQty, setOpeningQty] = useState(0);
  const [reorderLevel, setReorderLevel] = useState(0);
  const [description, setDescription] = useState("");

  // Edit item dialog
  const [editItem, setEditItem] = useState<StockItem | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  // Price intelligence dialog
  const [selectedPriceHistoryItem, setSelectedPriceHistoryItem] = useState<StockItem | null>(null);

  // Batch dialog (Goods only)
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchItemId, setBatchItemId] = useState("");
  const [batchNum, setBatchNum] = useState("");
  const [batchQty, setBatchQty] = useState(0);
  const [batchRate, setBatchRate] = useState(0);
  const [batchMfg, setBatchMfg] = useState("");
  const [batchExpiry, setBatchExpiry] = useState("");

  // Adjustment dialog (Goods only)
  const [adjOpen, setAdjOpen] = useState(false);
  const [adjItemId, setAdjItemId] = useState("");
  const [adjQty, setAdjQty] = useState(0);
  const [adjRate, setAdjRate] = useState(0);
  const [adjDir, setAdjDir] = useState<"in" | "out">("in");
  const [adjNarration, setAdjNarration] = useState("");

  // Transfer dialog (Goods only)
  const [trfOpen, setTrfOpen] = useState(false);
  const [trfFrom, setTrfFrom] = useState("");
  const [trfTo, setTrfTo] = useState("");
  const [trfQty, setTrfQty] = useState(0);
  const [trfRate, setTrfRate] = useState(0);

  const summary = getStockSummary();
  const lowStockItems = getLowStockItems();
  const expiringBatches = getExpiringBatches(60);

  // Filtered physical goods only (for batch / adjust / transfer)
  const physicalGoodsList = useMemo(() => {
    return stockItems.filter((i) => i.itemType !== "service");
  }, [stockItems]);

  // Combined categories for selector
  const availableCategories = useMemo(() => {
    const fromGroups = stockGroups.map((g) => g.name);
    const defaults = itemType === "service" ? DEFAULT_SERVICE_CATEGORIES : DEFAULT_GOODS_CATEGORIES;
    return Array.from(new Set([...defaults, ...fromGroups])).sort();
  }, [stockGroups, itemType]);

  // Filtered items list
  const filteredItems = useMemo(() => {
    return stockItems
      .filter((i) => {
        // Tab filter
        if (activeTab === "goods" && i.itemType === "service") return false;
        if (activeTab === "services" && i.itemType !== "service") return false;

        // Category filter
        if (categoryFilter !== "all" && i.stockGroup !== categoryFilter) return false;

        // Stock status filter (applies only to goods)
        if (stockStatusFilter !== "all") {
          if (i.itemType === "service") return false;
          if (stockStatusFilter === "in_stock" && i.currentQty <= 0) return false;
          if (stockStatusFilter === "low_stock" && (!i.reorderLevel || i.currentQty > i.reorderLevel)) return false;
          if (stockStatusFilter === "out_of_stock" && i.currentQty > 0) return false;
        }

        // Search query
        if (search.trim()) {
          const q = search.toLowerCase().trim();
          const matches =
            (i.name || "").toLowerCase().includes(q) ||
            (i.hsnCode && i.hsnCode.toLowerCase().includes(q)) ||
            (i.sacCode && i.sacCode.toLowerCase().includes(q)) ||
            (i.stockGroup && i.stockGroup.toLowerCase().includes(q)) ||
            (i.description && i.description.toLowerCase().includes(q));
          if (!matches) return false;
        }

        return true;
      })
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [stockItems, activeTab, categoryFilter, stockStatusFilter, search]);

  // Open create modal with type preset
  const handleOpenCreate = (type: ItemType = "goods") => {
    setItemType(type);
    setName("");
    setStockGroup(type === "service" ? DEFAULT_SERVICE_CATEGORIES[0] : DEFAULT_GOODS_CATEGORIES[0]);
    setCustomGroup("");
    setUnit(type === "service" ? "Projects" : "Nos");
    setHsnCode("");
    setSacCode(type === "service" ? "998314" : "");
    setBillingMethod("one-time");
    setGstRate(18);
    setStandardRate(0);
    setCostRate(0);
    setOpeningQty(0);
    setReorderLevel(0);
    setDescription("");
    setOpen(true);
  };

  const handleCreateItem = () => {
    if (!name.trim()) {
      toast({ title: "Error", description: "Item or Service name is required", variant: "destructive" });
      return;
    }

    const finalGroup = customGroup.trim() || stockGroup || (itemType === "service" ? "Services" : "General");
    const isService = itemType === "service";

    const item = addStockItem({
      name: name.trim(),
      itemType,
      stockGroup: finalGroup,
      unit,
      hsnCode: isService ? "" : hsnCode.trim().slice(0, 8),
      sacCode: isService ? sacCode.trim().slice(0, 8) : "",
      billingMethod: isService ? billingMethod : undefined,
      standardRate,
      costRate,
      description: description.trim() || undefined,
      gstRate,
      openingQty: isService ? 0 : openingQty,
      openingRate: costRate,
      openingValue: isService ? 0 : openingQty * costRate,
      currentQty: isService ? 0 : openingQty,
      currentRate: standardRate,
      currentValue: isService ? 0 : openingQty * costRate,
      reorderLevel: isService ? undefined : (reorderLevel || undefined),
    });

    if (!isService && openingQty > 0) {
      recordMovement(item.id, "Opening", openingQty, costRate, undefined, "Opening Stock");
    }

    toast({
      title: `${isService ? "Service" : "Goods Item"} Created`,
      description: `"${name}" added successfully.`,
    });
    setOpen(false);
  };

  const handleEditClick = (item: StockItem) => {
    setEditItem({ ...item });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editItem || !editItem.name.trim()) return;
    await updateStockItem(editItem.id, editItem);
    toast({ title: "Item Updated", description: `"${editItem.name}" saved.` });
    setEditOpen(false);
    setEditItem(null);
  };

  const handleDeleteItem = async (item: StockItem) => {
    if (confirm(`Are you sure you want to delete "${item.name}"?`)) {
      await deleteStockItem(item.id);
      toast({ title: "Item Deleted", description: `"${item.name}" removed from catalog.` });
    }
  };

  const handleCreateBatch = () => {
    if (!batchItemId || !batchNum.trim() || batchQty <= 0) {
      toast({ title: "Error", description: "Fill all required batch fields", variant: "destructive" });
      return;
    }
    addBatch(batchItemId, batchNum.trim(), batchQty, batchRate, batchMfg || undefined, batchExpiry || undefined);
    toast({ title: "Batch Created", description: `Batch "${batchNum}" registered.` });
    setBatchOpen(false);
    setBatchNum(""); setBatchQty(0); setBatchRate(0); setBatchMfg(""); setBatchExpiry("");
  };

  const handleAdjust = () => {
    if (!adjItemId || adjQty <= 0) {
      toast({ title: "Error", description: "Select goods item and enter quantity", variant: "destructive" });
      return;
    }
    adjustStock(adjItemId, adjQty, adjRate, adjDir, adjNarration || undefined);
    toast({ title: "Stock Adjusted", description: `${adjDir === "in" ? "Added" : "Removed"} ${adjQty} units` });
    setAdjOpen(false);
    setAdjQty(0); setAdjRate(0); setAdjNarration("");
  };

  const handleTransfer = () => {
    if (!trfFrom || !trfTo || trfFrom === trfTo || trfQty <= 0) {
      toast({ title: "Error", description: "Select different goods items and enter quantity", variant: "destructive" });
      return;
    }
    transferStock(trfFrom, trfTo, trfQty, trfRate);
    toast({ title: "Stock Transferred", description: `${trfQty} units transferred.` });
    setTrfOpen(false);
    setTrfQty(0); setTrfRate(0);
  };

  const inputCls = "w-full px-3 py-2 text-xs rounded-md border border-border bg-card outline-none focus:ring-1 focus:ring-accent";
  const labelCls = "text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1";

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between flex-wrap gap-4 pb-2 border-b border-border/40">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground flex items-center gap-2.5">
            <span>Products & Services Inventory</span>
            <Badge variant="outline" className="text-[11px] font-normal text-muted-foreground border-border/60">
              Goods & Services Engine
            </Badge>
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Integrated physical stock tracking with batch controls, plus digital service catalog with Indian GST SAC codes
          </p>
        </div>

        {perms.canCreate && (
          <div className="flex gap-2 flex-wrap items-center">
            {/* Stock Adjust (Goods only) */}
            <Dialog open={adjOpen} onOpenChange={setAdjOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5">
                  <RefreshCw className="h-3.5 w-3.5 text-muted-foreground" />
                  Adjust Stock
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-display">Physical Stock Adjustment</DialogTitle>
                  <CardDescription className="text-xs">
                    Manually add or remove physical inventory units (Goods only).
                  </CardDescription>
                </DialogHeader>
                <div className="space-y-3.5 pt-2">
                  <div>
                    <label className={labelCls}>Goods Item *</label>
                    <Select value={adjItemId} onValueChange={setAdjItemId}>
                      <SelectTrigger className="text-xs h-9"><SelectValue placeholder="Select goods item..." /></SelectTrigger>
                      <SelectContent>
                        {physicalGoodsList.map((i) => (
                          <SelectItem key={i.id} value={i.id} className="text-xs">
                            {i.name} (Stock: {i.currentQty} {i.unit})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className={labelCls}>Direction *</label>
                    <Select value={adjDir} onValueChange={(v) => setAdjDir(v as "in" | "out")}>
                      <SelectTrigger className="text-xs h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="in" className="text-xs text-emerald-600 font-medium">Stock In (Add)</SelectItem>
                        <SelectItem value="out" className="text-xs text-rose-600 font-medium">Stock Out (Deduct)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Quantity</label>
                      <input type="number" min="0" value={adjQty || ""} onChange={(e) => setAdjQty(Number(e.target.value))} className={inputCls} placeholder="0" />
                    </div>
                    <div>
                      <label className={labelCls}>Unit Cost Rate (₹)</label>
                      <input type="number" min="0" value={adjRate || ""} onChange={(e) => setAdjRate(Number(e.target.value))} className={inputCls} placeholder="0.00" />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Narration / Reason</label>
                    <input value={adjNarration} onChange={(e) => setAdjNarration(e.target.value)} className={inputCls} placeholder="e.g. Physical inventory reconciliation" maxLength={200} />
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" size="sm" onClick={() => setAdjOpen(false)}>Cancel</Button>
                    <Button size="sm" onClick={handleAdjust} className="bg-accent text-accent-foreground">Submit Adjustment</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Stock Transfer (Goods only) */}
            <Dialog open={trfOpen} onOpenChange={setTrfOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5">
                  <ArrowRightLeft className="h-3.5 w-3.5 text-muted-foreground" />
                  Transfer
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-display">Stock Transfer / Conversion</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 pt-2">
                  <div>
                    <label className={labelCls}>From Goods Item</label>
                    <Select value={trfFrom} onValueChange={setTrfFrom}>
                      <SelectTrigger className="text-xs h-9"><SelectValue placeholder="Source item..." /></SelectTrigger>
                      <SelectContent>
                        {physicalGoodsList.map((i) => (
                          <SelectItem key={i.id} value={i.id} className="text-xs">{i.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className={labelCls}>To Goods Item</label>
                    <Select value={trfTo} onValueChange={setTrfTo}>
                      <SelectTrigger className="text-xs h-9"><SelectValue placeholder="Destination item..." /></SelectTrigger>
                      <SelectContent>
                        {physicalGoodsList.filter((i) => i.id !== trfFrom).map((i) => (
                          <SelectItem key={i.id} value={i.id} className="text-xs">{i.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Quantity</label>
                      <input type="number" min="0" value={trfQty || ""} onChange={(e) => setTrfQty(Number(e.target.value))} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Rate (₹)</label>
                      <input type="number" min="0" value={trfRate || ""} onChange={(e) => setTrfRate(Number(e.target.value))} className={inputCls} />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" size="sm" onClick={() => setTrfOpen(false)}>Cancel</Button>
                    <Button size="sm" onClick={handleTransfer} className="bg-accent text-accent-foreground">Complete Transfer</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* New Batch (Goods only) */}
            <Dialog open={batchOpen} onOpenChange={setBatchOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5">
                  <Layers className="h-3.5 w-3.5 text-muted-foreground" />
                  New Batch
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-display">Register Batch Lot</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 pt-2">
                  <div>
                    <label className={labelCls}>Stock Item</label>
                    <Select value={batchItemId} onValueChange={setBatchItemId}>
                      <SelectTrigger className="text-xs h-9"><SelectValue placeholder="Select physical goods..." /></SelectTrigger>
                      <SelectContent>
                        {physicalGoodsList.map((i) => (
                          <SelectItem key={i.id} value={i.id} className="text-xs">{i.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className={labelCls}>Batch Number</label>
                    <input value={batchNum} onChange={(e) => setBatchNum(e.target.value)} className={inputCls} placeholder="e.g. BATCH-2026-001" maxLength={50} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Quantity</label>
                      <input type="number" min="0" value={batchQty || ""} onChange={(e) => setBatchQty(Number(e.target.value))} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Rate (₹)</label>
                      <input type="number" min="0" value={batchRate || ""} onChange={(e) => setBatchRate(Number(e.target.value))} className={inputCls} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Mfg Date</label>
                      <input type="date" value={batchMfg} onChange={(e) => setBatchMfg(e.target.value)} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Expiry Date</label>
                      <input type="date" value={batchExpiry} onChange={(e) => setBatchExpiry(e.target.value)} className={inputCls} />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" size="sm" onClick={() => setBatchOpen(false)}>Cancel</Button>
                    <Button size="sm" onClick={handleCreateBatch} className="bg-accent text-accent-foreground">Create Batch</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Reconcile Stock Action */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setReconcileDialogOpen(true)}
              className="h-8 text-xs gap-1.5 border-blue-500/30 hover:bg-blue-500/10 text-foreground"
            >
              <ShieldCheck className="h-3.5 w-3.5 text-blue-500" />
              Audit & Reconcile
            </Button>

            {/* Primary Action: Add Item / Service */}
            <Button onClick={() => handleOpenCreate("goods")} size="sm" className="h-8 text-xs gap-1.5 bg-primary text-primary-foreground">
              <Plus className="h-3.5 w-3.5" />
              Add Product / Service
            </Button>
          </div>
        )}
      </div>

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="border-border/60 shadow-xs">
          <CardContent className="p-3.5 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <Package className="h-4.5 w-4.5" />
            </div>
            <div className="min-w-0">
              <p className="text-lg font-bold font-display text-foreground leading-none">{summary.totalItems}</p>
              <p className="text-[11px] text-muted-foreground mt-1">Total Catalog</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/60 shadow-xs">
          <CardContent className="p-3.5 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-500/10 text-blue-600 flex items-center justify-center shrink-0">
              <IndianRupee className="h-4.5 w-4.5" />
            </div>
            <div className="min-w-0">
              <p className="text-lg font-bold font-display text-foreground leading-none truncate">{formatINR(summary.totalValue)}</p>
              <p className="text-[11px] text-muted-foreground mt-1">Physical Stock Asset ({summary.goodsCount})</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/60 shadow-xs">
          <CardContent className="p-3.5 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-purple-500/10 text-purple-600 flex items-center justify-center shrink-0">
              <Zap className="h-4.5 w-4.5" />
            </div>
            <div className="min-w-0">
              <p className="text-lg font-bold font-display text-foreground leading-none">{summary.servicesCount}</p>
              <p className="text-[11px] text-muted-foreground mt-1">Services Catalog</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/60 shadow-xs">
          <CardContent className="p-3.5 flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${summary.lowStock > 0 ? "bg-amber-500/10 text-amber-600" : "bg-muted text-muted-foreground"}`}>
              <TrendingDown className="h-4.5 w-4.5" />
            </div>
            <div className="min-w-0">
              <p className={`text-lg font-bold font-display leading-none ${summary.lowStock > 0 ? "text-amber-600" : "text-foreground"}`}>
                {summary.lowStock}
              </p>
              <p className="text-[11px] text-muted-foreground mt-1">Low Stock Alerts</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/60 shadow-xs">
          <CardContent className="p-3.5 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-info/10 text-info flex items-center justify-center shrink-0">
              <Layers className="h-4.5 w-4.5" />
            </div>
            <div className="min-w-0">
              <p className="text-lg font-bold font-display text-foreground leading-none">{summary.totalBatches}</p>
              <p className="text-[11px] text-muted-foreground mt-1">Active Batches</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs & Filters */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 flex-wrap">
          <TabsList className="h-9 p-1 bg-muted/60">
            <TabsTrigger value="all" className="text-xs px-3">
              All ({summary.totalItems})
            </TabsTrigger>
            <TabsTrigger value="goods" className="text-xs px-3 gap-1">
              <Package className="h-3 w-3 text-blue-500" />
              Goods ({summary.goodsCount})
            </TabsTrigger>
            <TabsTrigger value="services" className="text-xs px-3 gap-1">
              <Zap className="h-3 w-3 text-purple-500" />
              Services ({summary.servicesCount})
            </TabsTrigger>
            <TabsTrigger value="batches" className="text-xs px-3">
              Batches ({summary.totalBatches})
            </TabsTrigger>
            <TabsTrigger value="movements" className="text-xs px-3">
              Movements
            </TabsTrigger>
            <TabsTrigger value="valuation" className="text-xs px-3">
              Valuation & Reports
            </TabsTrigger>
          </TabsList>

          {/* Search & Category Filter */}
          {(activeTab === "all" || activeTab === "goods" || activeTab === "services") && (
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative min-w-[200px] max-w-xs">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name, HSN, SAC..."
                  className="h-8 text-xs pl-8 bg-card"
                />
              </div>

              {/* Category Filter */}
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="h-8 text-xs w-[140px] bg-card">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent className="max-h-56">
                  <SelectItem value="all" className="text-xs">All Categories</SelectItem>
                  {Array.from(new Set(stockItems.map((i) => i.stockGroup || "General"))).sort().map((cat) => (
                    <SelectItem key={cat} value={cat} className="text-xs">{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Stock filter (for Goods or All) */}
              {activeTab !== "services" && (
                <Select value={stockStatusFilter} onValueChange={setStockStatusFilter}>
                  <SelectTrigger className="h-8 text-xs w-[130px] bg-card">
                    <SelectValue placeholder="Stock Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" className="text-xs">All Stock</SelectItem>
                    <SelectItem value="in_stock" className="text-xs">In Stock</SelectItem>
                    <SelectItem value="low_stock" className="text-xs text-amber-600">Low Stock</SelectItem>
                    <SelectItem value="out_of_stock" className="text-xs text-rose-600">Out of Stock</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
        </div>

        {/* ITEMS & SERVICES TABLE (Tab: all, goods, services) */}
        {(activeTab === "all" || activeTab === "goods" || activeTab === "services") && (
          <Card className="border-border/60 shadow-xs">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/40 hover:bg-muted/40">
                      <TableHead className="text-xs font-semibold">Type</TableHead>
                      <TableHead className="text-xs font-semibold">Item / Service Name</TableHead>
                      <TableHead className="text-xs font-semibold">Category</TableHead>
                      <TableHead className="text-xs font-semibold">HSN / SAC</TableHead>
                      <TableHead className="text-xs font-semibold text-right">Available Stock</TableHead>
                      <TableHead className="text-xs font-semibold text-right">Avg Cost (WAC)</TableHead>
                      <TableHead className="text-xs font-semibold text-right">Last Purchase</TableHead>
                      <TableHead className="text-xs font-semibold text-right">Last Sold Rate</TableHead>
                      <TableHead className="text-xs font-semibold text-right">Valuation</TableHead>
                      <TableHead className="text-xs font-semibold text-center">GST %</TableHead>
                      <TableHead className="text-xs font-semibold text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="py-12 text-center">
                          <div className="flex flex-col items-center justify-center gap-2 max-w-sm mx-auto">
                            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-muted-foreground">
                              <Package className="h-5 w-5" />
                            </div>
                            <h3 className="text-sm font-semibold text-foreground">No items or services found</h3>
                            <p className="text-xs text-muted-foreground">
                              {search || categoryFilter !== "all" || stockStatusFilter !== "all"
                                ? "Try adjusting your filters or search keyword."
                                : "Add your first physical product or digital service to get started."}
                            </p>
                            {perms.canCreate && (
                              <div className="flex gap-2 pt-2">
                                <Button size="sm" variant="outline" onClick={() => handleOpenCreate("goods")} className="text-xs gap-1">
                                  <Package className="h-3.5 w-3.5 text-blue-500" />
                                  Add Goods
                                </Button>
                                <Button size="sm" onClick={() => handleOpenCreate("service")} className="text-xs gap-1 bg-purple-600 text-white hover:bg-purple-700">
                                  <Zap className="h-3.5 w-3.5" />
                                  Add Service
                                </Button>
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredItems.map((item) => {
                        const isService = item.itemType === "service";
                        const isLow = !isService && !!(item.reorderLevel && item.currentQty <= item.reorderLevel);
                        const isOut = !isService && item.currentQty <= 0;

                        return (
                          <TableRow key={item.id} className="hover:bg-muted/30 transition-colors">
                            {/* Type Badge */}
                            <TableCell className="py-3">
                              {isService ? (
                                <Badge
                                  variant="outline"
                                  className="text-[10px] px-1.5 py-0.5 bg-purple-500/10 text-purple-600 border-purple-500/30 uppercase font-bold tracking-wider"
                                >
                                  Service
                                </Badge>
                              ) : (
                                <Badge
                                  variant="outline"
                                  className="text-[10px] px-1.5 py-0.5 bg-blue-500/10 text-blue-600 border-blue-500/30 uppercase font-bold tracking-wider"
                                >
                                  Goods
                                </Badge>
                              )}
                            </TableCell>

                            {/* Name & Details */}
                            <TableCell className="py-3 min-w-[200px]">
                              <div>
                                <span className="font-semibold text-xs text-foreground block">
                                  {item.name}
                                </span>
                                {item.description && (
                                  <span className="text-[11px] text-muted-foreground truncate block max-w-xs" title={item.description}>
                                    {item.description}
                                  </span>
                                )}
                                {isService && item.billingMethod && (
                                  <span className="text-[10px] text-purple-600 dark:text-purple-400 capitalize inline-block mt-0.5">
                                    Billing: {item.billingMethod} · Unit: {item.unit}
                                  </span>
                                )}
                              </div>
                            </TableCell>

                            {/* Category */}
                            <TableCell className="py-3 text-xs text-muted-foreground">
                              {item.stockGroup || "—"}
                            </TableCell>

                            {/* HSN / SAC Code */}
                            <TableCell className="py-3 font-mono text-xs">
                              {isService ? (
                                <span className="text-purple-600/90 dark:text-purple-400">
                                  {item.sacCode || item.hsnCode || "—"}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">
                                  {item.hsnCode || "—"}
                                </span>
                              )}
                            </TableCell>

                            {/* Stock Quantity */}
                            <TableCell className="py-3 text-right">
                              {isService ? (
                                <Badge
                                  variant="outline"
                                  className="text-[10px] px-2 py-0.5 bg-purple-500/10 text-purple-600 border-purple-500/30 font-medium whitespace-nowrap"
                                >
                                  Non-Stock Service
                                </Badge>
                              ) : (
                                <div className="space-y-0.5">
                                  <div className="font-semibold text-xs flex items-center justify-end gap-1.5">
                                    <span className={isOut ? "text-rose-600" : isLow ? "text-amber-600" : "text-foreground"}>
                                      {item.currentQty} {item.unit}
                                    </span>
                                    {isLow && !isOut && (
                                      <span title="Low Stock">
                                        <AlertTriangle className="h-3 w-3 text-amber-500" />
                                      </span>
                                    )}
                                  </div>
                                  {item.reorderLevel ? (
                                    <span className="text-[10px] text-muted-foreground block">
                                      Min: {item.reorderLevel}
                                    </span>
                                  ) : null}
                                </div>
                              )}
                            </TableCell>

                            {/* Avg Purchase Cost (WAC) */}
                            <TableCell className="py-3 text-right font-mono text-xs">
                              <span className="font-semibold text-foreground">
                                {formatINR(item.currentRate || item.costRate || 0)}
                              </span>
                              <span className="text-[10px] text-muted-foreground block">Avg WAC</span>
                            </TableCell>

                            {/* Last Purchase Rate */}
                            <TableCell className="py-3 text-right font-mono text-xs">
                              {item.lastPurchaseRate !== null && item.lastPurchaseRate !== undefined ? (
                                <div>
                                  <span className="font-semibold text-foreground">{formatINR(item.lastPurchaseRate)}</span>
                                  {item.lastPurchaseParty && (
                                    <span className="text-[10px] text-muted-foreground block truncate max-w-[120px] ml-auto" title={item.lastPurchaseParty}>
                                      {item.lastPurchaseParty}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-[11px] text-muted-foreground italic">—</span>
                              )}
                            </TableCell>

                            {/* Last Sold Rate */}
                            <TableCell className="py-3 text-right font-mono text-xs">
                              {item.lastSellingRate !== null && item.lastSellingRate !== undefined ? (
                                <div>
                                  <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                                    {formatINR(item.lastSellingRate)}
                                  </span>
                                  {item.lastSoldParty && (
                                    <span className="text-[10px] text-muted-foreground block truncate max-w-[120px] ml-auto" title={item.lastSoldParty}>
                                      {item.lastSoldParty}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <Badge variant="outline" className="text-[10px] bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/25 font-normal">
                                  Not Sold Yet
                                </Badge>
                              )}
                            </TableCell>

                            {/* Stock Valuation */}
                            <TableCell className="py-3 text-right font-mono text-xs font-bold text-foreground">
                              {isService ? "—" : formatINR(item.currentValue || (item.currentQty * (item.currentRate || 0)))}
                            </TableCell>

                            {/* GST Rate */}
                            <TableCell className="py-3 text-center">
                              <Badge variant="outline" className="text-[10px] bg-muted/60">
                                {item.gstRate}%
                              </Badge>
                            </TableCell>

                            {/* Actions */}
                            <TableCell className="py-3 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-primary hover:text-primary hover:bg-primary/10"
                                  onClick={() => setSelectedPriceHistoryItem(item)}
                                  title="Price History, Supplier Comparison & Profitability"
                                >
                                  <TrendingUp className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                                  onClick={() => handleEditClick(item)}
                                  title="Edit Item"
                                >
                                  <Edit className="h-3.5 w-3.5" />
                                </Button>
                                {perms.canCreate && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-muted-foreground hover:text-rose-500"
                                    onClick={() => handleDeleteItem(item)}
                                    title="Delete Item"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* BATCHES TAB (Physical Goods only) */}
        <TabsContent value="batches">
          <Card className="border-border/60 shadow-xs">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base font-display">Batch & Lot Tracking</CardTitle>
                  <CardDescription className="text-xs">
                    Track manufacturing dates, expiration dates, and lot quantities for physical inventory.
                  </CardDescription>
                </div>
                <Button size="sm" onClick={() => setBatchOpen(true)} className="text-xs gap-1">
                  <Plus className="h-3.5 w-3.5" /> New Batch
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/40">
                      <TableHead className="text-xs font-semibold">Batch Number</TableHead>
                      <TableHead className="text-xs font-semibold">Stock Item</TableHead>
                      <TableHead className="text-xs font-semibold text-right">Quantity</TableHead>
                      <TableHead className="text-xs font-semibold text-right">Unit Rate</TableHead>
                      <TableHead className="text-xs font-semibold text-right">Batch Value</TableHead>
                      <TableHead className="text-xs font-semibold">Mfg Date</TableHead>
                      <TableHead className="text-xs font-semibold">Expiry Date</TableHead>
                      <TableHead className="text-xs font-semibold text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {batches.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="py-8 text-center text-xs text-muted-foreground">
                          No active batches registered. Batches apply to physical goods.
                        </TableCell>
                      </TableRow>
                    ) : (
                      batches.map((b) => {
                        const item = stockItems.find((i) => i.id === b.stockItemId);
                        const isExpiring = b.expiryDate && new Date(b.expiryDate) <= new Date(Date.now() + 60 * 24 * 60 * 60 * 1000);
                        const isExpired = b.expiryDate && new Date(b.expiryDate) < new Date();

                        return (
                          <TableRow key={b.id} className="hover:bg-muted/30">
                            <TableCell className="text-xs font-mono font-medium">{b.batchNumber}</TableCell>
                            <TableCell className="text-xs">{item?.name || b.stockItemId}</TableCell>
                            <TableCell className="text-xs font-semibold text-right">{b.quantity}</TableCell>
                            <TableCell className="text-xs text-right font-mono">{formatINR(b.rate)}</TableCell>
                            <TableCell className="text-xs font-semibold text-right font-mono">{formatINR(b.value)}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{b.manufacturingDate || "—"}</TableCell>
                            <TableCell className="text-xs">
                              {b.expiryDate ? (
                                <span className={isExpired ? "text-destructive font-medium" : isExpiring ? "text-amber-600 font-medium" : "text-muted-foreground"}>
                                  {b.expiryDate} {isExpired && "⚠"}
                                </span>
                              ) : "—"}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge
                                variant="outline"
                                className={`text-[10px] ${
                                  b.status === "Active"
                                    ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20"
                                    : "bg-destructive/10 text-destructive border-destructive/20"
                                }`}
                              >
                                {b.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* MOVEMENTS TAB */}
        <TabsContent value="movements">
          <Card className="border-border/60 shadow-xs">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-display">Physical Stock Movements</CardTitle>
              <CardDescription className="text-xs">
                Audit trail of inbound purchases, adjustments, outbound sales, and stock transfers.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto max-h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/40">
                      <TableHead className="text-xs font-semibold">Date</TableHead>
                      <TableHead className="text-xs font-semibold">Item</TableHead>
                      <TableHead className="text-xs font-semibold">Type</TableHead>
                      <TableHead className="text-xs font-semibold text-right">Quantity</TableHead>
                      <TableHead className="text-xs font-semibold text-right">Rate</TableHead>
                      <TableHead className="text-xs font-semibold text-right">Value</TableHead>
                      <TableHead className="text-xs font-semibold">Reference</TableHead>
                      <TableHead className="text-xs font-semibold">Narration</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {movements.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="py-8 text-center text-xs text-muted-foreground">
                          No stock movements recorded yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      movements.slice(0, 50).map((mv) => {
                        const item = stockItems.find((i) => i.id === mv.stockItemId);
                        const isIn = mv.type.includes("In") || mv.type === "Purchase" || mv.type === "Opening";

                        return (
                          <TableRow key={mv.id} className="hover:bg-muted/30">
                            <TableCell className="text-xs whitespace-nowrap">{mv.date}</TableCell>
                            <TableCell className="text-xs font-medium">{item?.name || mv.stockItemId}</TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={`text-[10px] ${
                                  isIn
                                    ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20"
                                    : "bg-rose-500/10 text-rose-600 border-rose-500/20"
                                }`}
                              >
                                {mv.type}
                              </Badge>
                            </TableCell>
                            <TableCell className={`text-xs font-semibold text-right font-mono ${isIn ? "text-emerald-600" : "text-rose-600"}`}>
                              {isIn ? "+" : "−"}{mv.quantity}
                            </TableCell>
                            <TableCell className="text-xs text-right font-mono">{formatINR(mv.rate)}</TableCell>
                            <TableCell className="text-xs font-semibold text-right font-mono">{formatINR(mv.value)}</TableCell>
                            <TableCell className="text-xs font-mono text-muted-foreground">{mv.reference || "—"}</TableCell>
                            <TableCell className="text-xs text-muted-foreground max-w-[160px] truncate">{mv.narration || "—"}</TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* VALUATION & REPORTS TAB */}
        <TabsContent value="valuation">
          <div className="grid lg:grid-cols-2 gap-5">
            {/* FIFO Inventory Valuation (Goods only) */}
            <Card className="border-border/60 shadow-xs">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-display flex items-center gap-2">
                  <Archive className="h-4 w-4 text-primary" />
                  FIFO Inventory Valuation
                </CardTitle>
                <CardDescription className="text-xs">
                  Valuation of physical inventory lots under First-In, First-Out (FIFO) costing.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {physicalGoodsList.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-4">No physical goods in catalog.</p>
                  ) : (
                    physicalGoodsList.map((item) => {
                      const layers = getFIFOLayers(item.id);
                      const fifoVal = layers.reduce((s, l) => s + l.quantity * l.rate, 0);

                      return (
                        <div key={item.id} className="border-b border-border/30 pb-2.5 last:border-0 last:pb-0">
                          <div className="flex justify-between items-center text-xs">
                            <span className="font-semibold text-foreground">{item.name}</span>
                            <span className="font-mono font-bold text-primary">{formatINR(fifoVal)}</span>
                          </div>
                          {layers.length > 0 ? (
                            <div className="mt-1 space-y-0.5">
                              {layers.map((l, i) => (
                                <div key={i} className="flex justify-between text-[11px] text-muted-foreground pl-2 font-mono">
                                  <span>Layer {i + 1}: {l.quantity} {item.unit} @ {formatINR(l.rate)}</span>
                                  <span>{formatINR(l.quantity * l.rate)}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-[11px] text-muted-foreground/70 pl-2">Zero stock balance</p>
                          )}
                        </div>
                      );
                    })
                  )}

                  <div className="flex justify-between pt-3 border-t border-border font-bold text-xs">
                    <span>Total Physical Inventory Valuation</span>
                    <span className="text-primary font-mono text-sm">
                      {formatINR(physicalGoodsList.reduce((s, i) => s + getFIFOValuation(i.id), 0))}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Low Stock Alerts */}
            <Card className="border-border/60 shadow-xs">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-display flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  Reorder Level Alerts
                </CardTitle>
                <CardDescription className="text-xs">
                  Physical items that require purchasing to prevent stockouts.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {lowStockItems.length === 0 ? (
                  <div className="py-8 text-center space-y-1">
                    <CheckCircle2 className="h-7 w-7 text-emerald-500 mx-auto" />
                    <p className="text-xs font-semibold text-foreground">All stock levels are optimal</p>
                    <p className="text-[11px] text-muted-foreground">No physical goods have breached reorder thresholds.</p>
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {lowStockItems.map((item) => (
                      <div key={item.id} className="flex items-center justify-between p-2.5 rounded-lg bg-amber-500/5 border border-amber-500/20">
                        <div>
                          <p className="text-xs font-semibold text-foreground">{item.name}</p>
                          <p className="text-[10px] text-muted-foreground">
                            Category: {item.stockGroup} · Reorder Threshold: {item.reorderLevel} {item.unit}
                          </p>
                        </div>
                        <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/30 text-xs font-bold">
                          {item.currentQty} {item.unit} remaining
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* CREATE ITEM / SERVICE DIALOG */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto p-5 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold font-display">
              Add New {itemType === "service" ? "Service" : "Goods Product"}
            </DialogTitle>
            <CardDescription className="text-xs">
              Configure product details, GST classification, billing rates, and inventory rules.
            </CardDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            {/* Primary Classification Switch */}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider block mb-1.5">
                Item Classification *
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setItemType("goods");
                    setUnit("Nos");
                  }}
                  className={`p-3 rounded-lg border text-xs font-medium flex items-center justify-center gap-2 transition-all ${
                    itemType === "goods"
                      ? "bg-blue-500/10 border-blue-500 text-blue-600 font-semibold shadow-xs"
                      : "border-border hover:bg-muted/40 text-muted-foreground"
                  }`}
                >
                  <Package className="h-4 w-4" />
                  Physical Goods (Inventory)
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setItemType("service");
                    setUnit("Projects");
                    setSacCode("998314");
                  }}
                  className={`p-3 rounded-lg border text-xs font-medium flex items-center justify-center gap-2 transition-all ${
                    itemType === "service"
                      ? "bg-purple-500/10 border-purple-500 text-purple-600 font-semibold shadow-xs"
                      : "border-border hover:bg-muted/40 text-muted-foreground"
                  }`}
                >
                  <Zap className="h-4 w-4" />
                  Service (Non-Physical)
                </button>
              </div>
            </div>

            {/* Name */}
            <div>
              <Label className="text-xs font-semibold mb-1 block">
                {itemType === "service" ? "Service Title *" : "Product Name *"}
              </Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={
                  itemType === "service"
                    ? "e.g. Website Development, AMC Maintenance, Web Hosting (1 Year)"
                    : "e.g. Dell Latitude 5420, Cisco SG350 Switch"
                }
                className="h-9 text-xs"
                maxLength={200}
                autoFocus
              />
            </div>

            {/* Category / Group */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold mb-1 block">
                  Category / Group
                </Label>
                <Select value={stockGroup} onValueChange={setStockGroup}>
                  <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Select Category" /></SelectTrigger>
                  <SelectContent className="max-h-56">
                    {availableCategories.map((c) => (
                      <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>
                    ))}
                    <SelectItem value="__custom__" className="text-xs font-semibold text-primary">+ Custom Category...</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs font-semibold mb-1 block">
                  {itemType === "service" ? "Billable Unit" : "Unit of Measurement"}
                </Label>
                <Select value={unit} onValueChange={setUnit}>
                  <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(itemType === "service" ? SERVICE_UNITS : GOODS_UNITS).map((u) => (
                      <SelectItem key={u} value={u} className="text-xs">{u}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {stockGroup === "__custom__" && (
              <div>
                <Label className="text-xs font-semibold mb-1 block">Custom Category Name</Label>
                <Input
                  value={customGroup}
                  onChange={(e) => setCustomGroup(e.target.value)}
                  placeholder="Enter new category name..."
                  className="h-9 text-xs"
                />
              </div>
            )}

            {/* Code & GST */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold mb-1 block">
                  {itemType === "service" ? "SAC Code (Services Accounting)" : "HSN Code (Goods)"}
                </Label>
                <Input
                  value={itemType === "service" ? sacCode : hsnCode}
                  onChange={(e) => {
                    const clean = e.target.value.replace(/[^0-9]/g, "").slice(0, 8);
                    if (itemType === "service") setSacCode(clean);
                    else setHsnCode(clean);
                  }}
                  placeholder={itemType === "service" ? "e.g. 998314" : "e.g. 84713010"}
                  className="h-9 text-xs font-mono"
                  maxLength={8}
                />
              </div>

              <div>
                <Label className="text-xs font-semibold mb-1 block">GST Rate (%)</Label>
                <Select value={String(gstRate)} onValueChange={(v) => setGstRate(Number(v))}>
                  <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[0, 5, 12, 18, 28].map((r) => (
                      <SelectItem key={r} value={String(r)} className="text-xs">{r}%</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Service-Specific Fields: Billing Method */}
            {itemType === "service" ? (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold mb-1 block">Billing Frequency / Method</Label>
                  <Select value={billingMethod} onValueChange={(v) => setBillingMethod(v as ServiceBillingMethod)}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="one-time" className="text-xs">One-Time Project</SelectItem>
                      <SelectItem value="hourly" className="text-xs">Hourly Rate</SelectItem>
                      <SelectItem value="daily" className="text-xs">Daily Retainer</SelectItem>
                      <SelectItem value="monthly" className="text-xs">Monthly Recurring</SelectItem>
                      <SelectItem value="yearly" className="text-xs">Yearly Contract</SelectItem>
                      <SelectItem value="subscription" className="text-xs">Subscription</SelectItem>
                      <SelectItem value="custom" className="text-xs">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs font-semibold mb-1 block">Standard Selling Fee (₹)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={standardRate || ""}
                    onChange={(e) => setStandardRate(Number(e.target.value))}
                    placeholder="0.00"
                    className="h-9 text-xs font-mono"
                  />
                </div>
              </div>
            ) : (
              /* Physical Goods Fields: Opening Stock & Reorder */
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold mb-1 block">Selling Price (₹)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={standardRate || ""}
                      onChange={(e) => setStandardRate(Number(e.target.value))}
                      placeholder="0.00"
                      className="h-9 text-xs font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold mb-1 block">Purchase Cost Rate (₹)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={costRate || ""}
                      onChange={(e) => setCostRate(Number(e.target.value))}
                      placeholder="0.00"
                      className="h-9 text-xs font-mono"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold mb-1 block">Opening Stock Quantity</Label>
                    <Input
                      type="number"
                      min="0"
                      value={openingQty || ""}
                      onChange={(e) => setOpeningQty(Number(e.target.value))}
                      placeholder="0"
                      className="h-9 text-xs"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold mb-1 block">Reorder Alert Threshold</Label>
                    <Input
                      type="number"
                      min="0"
                      value={reorderLevel || ""}
                      onChange={(e) => setReorderLevel(Number(e.target.value))}
                      placeholder="e.g. 5"
                      className="h-9 text-xs"
                    />
                  </div>
                </div>

                {openingQty > 0 && (
                  <div className="p-2.5 bg-muted/60 rounded-md border border-border/50 text-xs flex justify-between">
                    <span className="text-muted-foreground">Opening Stock Value:</span>
                    <span className="font-bold font-mono text-foreground">{formatINR(openingQty * costRate)}</span>
                  </div>
                )}
              </>
            )}

            {/* Description / Scope */}
            <div>
              <Label className="text-xs font-semibold mb-1 block">Description & Scope</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief notes or service deliverables for invoices..."
                rows={2}
                className="text-xs"
              />
            </div>

            {itemType === "service" && (
              <div className="p-2.5 rounded-lg bg-purple-500/5 border border-purple-500/20 text-[11px] text-muted-foreground">
                <span className="font-bold text-foreground">💡 Service Rule:</span> Non-physical items bypass warehousing, stock movements, and inventory valuations. Invoices can bill unlimited hours or projects without physical stock checks.
              </div>
            )}
          </div>

          <DialogFooter className="pt-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleCreateItem} className="bg-primary text-primary-foreground gap-1.5">
              <Plus className="h-4 w-4" />
              Save {itemType === "service" ? "Service" : "Goods Product"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EDIT ITEM DIALOG */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-5 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold font-display">
              Edit {editItem?.itemType === "service" ? "Service" : "Product"}
            </DialogTitle>
          </DialogHeader>

          {editItem && (
            <div className="space-y-4 pt-2">
              <div>
                <Label className="text-xs font-semibold mb-1 block">Name *</Label>
                <Input
                  value={editItem.name || ""}
                  onChange={(e) => setEditItem({ ...editItem, name: e.target.value })}
                  className="h-9 text-xs"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold mb-1 block">Category</Label>
                  <Input
                    value={editItem.stockGroup || ""}
                    onChange={(e) => setEditItem({ ...editItem, stockGroup: e.target.value })}
                    className="h-9 text-xs"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold mb-1 block">Unit</Label>
                  <Input
                    value={editItem.unit || ""}
                    onChange={(e) => setEditItem({ ...editItem, unit: e.target.value })}
                    className="h-9 text-xs"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold mb-1 block">
                    {editItem.itemType === "service" ? "SAC Code" : "HSN Code"}
                  </Label>
                  <Input
                    value={editItem.itemType === "service" ? (editItem.sacCode || "") : (editItem.hsnCode || "")}
                    onChange={(e) => {
                      const clean = e.target.value.replace(/[^0-9]/g, "").slice(0, 8);
                      setEditItem(editItem.itemType === "service" ? { ...editItem, sacCode: clean } : { ...editItem, hsnCode: clean });
                    }}
                    className="h-9 text-xs font-mono"
                    maxLength={8}
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold mb-1 block">GST Rate (%)</Label>
                  <Select
                    value={String(editItem.gstRate || 18)}
                    onValueChange={(v) => setEditItem({ ...editItem, gstRate: Number(v) })}
                  >
                    <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[0, 5, 12, 18, 28].map((r) => (
                        <SelectItem key={r} value={String(r)} className="text-xs">{r}%</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold mb-1 block">Selling Rate (₹)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editItem.standardRate ?? editItem.currentRate ?? ""}
                    onChange={(e) => setEditItem({ ...editItem, standardRate: Number(e.target.value), currentRate: Number(e.target.value) })}
                    className="h-9 text-xs font-mono"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold mb-1 block">Cost / Purchase Rate (₹)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editItem.costRate ?? editItem.openingRate ?? ""}
                    onChange={(e) => setEditItem({ ...editItem, costRate: Number(e.target.value), openingRate: Number(e.target.value) })}
                    className="h-9 text-xs font-mono"
                  />
                </div>
              </div>

              {editItem.itemType === "service" ? (
                <div>
                  <Label className="text-xs font-semibold mb-1 block">Billing Frequency</Label>
                  <Select
                    value={editItem.billingMethod || "one-time"}
                    onValueChange={(v) => setEditItem({ ...editItem, billingMethod: v as ServiceBillingMethod })}
                  >
                    <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="one-time" className="text-xs">One-Time Project</SelectItem>
                      <SelectItem value="hourly" className="text-xs">Hourly Rate</SelectItem>
                      <SelectItem value="daily" className="text-xs">Daily Retainer</SelectItem>
                      <SelectItem value="monthly" className="text-xs">Monthly Recurring</SelectItem>
                      <SelectItem value="yearly" className="text-xs">Yearly Contract</SelectItem>
                      <SelectItem value="subscription" className="text-xs">Subscription</SelectItem>
                      <SelectItem value="custom" className="text-xs">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div>
                  <Label className="text-xs font-semibold mb-1 block">Reorder Alert Threshold</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editItem.reorderLevel ?? ""}
                    onChange={(e) => setEditItem({ ...editItem, reorderLevel: Number(e.target.value) || undefined })}
                    className="h-9 text-xs"
                  />
                </div>
              )}

              <div>
                <Label className="text-xs font-semibold mb-1 block">Description</Label>
                <Textarea
                  value={editItem.description || ""}
                  onChange={(e) => setEditItem({ ...editItem, description: e.target.value })}
                  rows={2}
                  className="text-xs"
                />
              </div>
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button variant="outline" size="sm" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleSaveEdit} className="bg-primary text-primary-foreground">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Price Intelligence & History Dialog */}
      <ProductPriceHistoryDialog
        item={selectedPriceHistoryItem}
        isOpen={!!selectedPriceHistoryItem}
        onOpenChange={(open) => {
          if (!open) setSelectedPriceHistoryItem(null);
        }}
      />

      {/* Stock Audit & Safe Reconciliation Dialog */}
      <InventoryReconciliationDialog
        open={reconcileDialogOpen}
        onOpenChange={setReconcileDialogOpen}
        companyId={activeCompany?.id || ""}
        onReconciliationCompleted={() => {
          reconcileInventory();
        }}
      />
    </div>
  );
}
